import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Loader2, Paperclip, FileText, Image, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/app/AppShell";

const GENDER_OPTIONS = ["Male", "Female", "Other"];
const COVERAGE_OPTIONS = ["Individual Only", "Individual + Spouse", "Individual + Children", "Entire Family"];
const INCOME_OPTIONS = ["Under $20,000","$20,000 - $30,000","$30,000 - $40,000","$40,000 - $50,000","$50,000 - $75,000","$75,000 - $100,000","Over $100,000"];
const STEPS = ["Partner Info", "Client & Case", "Review"];

function Field({ label, value, onChange, placeholder, multiline, type = "text", maxLength, autoCapitalize, testId }) {
  return (
    <div className="mb-3">
      <label className="label-up">{label}</label>
      {multiline ? (
        <textarea data-testid={testId} className="input-field min-h-[96px]" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input data-testid={testId} className="input-field" type={type} placeholder={placeholder} value={value} maxLength={maxLength} autoCapitalize={autoCapitalize} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function ChipGroup({ label, options, value, onChange, testIdPrefix }) {
  return (
    <div className="mb-3">
      <label className="label-up">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = value === o;
          return (
            <button type="button" key={o} data-testid={`${testIdPrefix}-${o.replace(/\s+/g, "_")}`} onClick={() => onChange(o)} className={`chip ${active ? "chip-outline" : ""}`}>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between mb-3 p-4 rounded-xl border" style={{ borderColor: "#E8E8E8", background: "#fff" }}>
      <span className="text-sm font-semibold flex-1 mr-3">{label}</span>
      <button type="button" data-testid={testId} onClick={() => onChange(!value)} className="w-12 h-7 rounded-full p-0.5 transition-colors" style={{ background: value ? "#D62828" : "#ccc" }}>
        <span className="block w-6 h-6 rounded-full bg-white transition-transform" style={{ transform: value ? "translateX(20px)" : "translateX(0)" }} />
      </button>
    </div>
  );
}

function ReviewBlock({ title, items }) {
  return (
    <div className="bg-white p-5 rounded-2xl mb-3 border" style={{ borderColor: "#E8E8E8" }}>
      <div className="eyebrow mb-3">{title}</div>
      {items.map(([k, v], i) => (
        <div key={i} className="flex items-start justify-between gap-2 py-1.5">
          <span className="text-xs font-bold flex-1" style={{ color: "#5A5A5A" }}>{k}</span>
          <span className="text-sm font-semibold flex-[2] text-right">{v || "—"}</span>
        </div>
      ))}
    </div>
  );
}

export default function Submit() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const editId = params.get("id");
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [pickingDoc, setPickingDoc] = useState(false);
  const [form, setForm] = useState({
    partner_name: user?.name || "",
    partner_agency: user?.agency || "",
    partner_phone: user?.phone || "",
    client_full_name: "", client_phone: "", date_of_birth: "", gender: "",
    resident_state: "", zip_code: "", household_size: "1",
    who_needs_coverage: "", estimated_annual_income: "", coverage_start_date: "",
    tobacco_user: false, declined_recently: false,
    major_health_conditions: "", current_prescriptions: "", detailed_case_notes: "",
    target_monthly_budget: "", coverage_goals: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const list = await api(`/referrals?status_filter=new`);
        const ex = list.find((r) => r.id === editId);
        if (ex) {
          setForm({
            partner_name: ex.partner_name || "", partner_agency: ex.partner_agency || "", partner_phone: ex.partner_phone || "",
            client_full_name: ex.client_full_name || "", client_phone: ex.client_phone || "",
            date_of_birth: ex.date_of_birth || "", gender: ex.gender || "",
            resident_state: ex.resident_state || "", zip_code: ex.zip_code || "",
            household_size: String(ex.household_size || 1),
            who_needs_coverage: ex.who_needs_coverage || "", estimated_annual_income: ex.estimated_annual_income || "",
            coverage_start_date: ex.coverage_start_date || "",
            tobacco_user: !!ex.tobacco_user, declined_recently: !!ex.declined_recently,
            major_health_conditions: ex.major_health_conditions || "", current_prescriptions: ex.current_prescriptions || "",
            detailed_case_notes: ex.detailed_case_notes || "", target_monthly_budget: ex.target_monthly_budget || "",
            coverage_goals: ex.coverage_goals || "",
          });
          setStep(1);
        }
      } catch {}
    })();
  }, [editId]);

  const validateStep = () => {
    if (step === 0) {
      if (!form.partner_name || !form.partner_phone) { alert("Please enter your name and phone number."); return false; }
    }
    if (step === 1) {
      if (!form.client_full_name || !form.client_phone || !form.date_of_birth || !form.gender || !form.resident_state || !form.zip_code || !form.who_needs_coverage) {
        alert("Please fill out all client fields marked required.");
        return false;
      }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, 2)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPickingDoc(true);
    try {
      const next = [];
      for (const f of files) {
        if (documents.length + next.length >= 4) { alert("You can attach up to 4 documents per referral."); break; }
        const sizeKb = Math.ceil(f.size / 1024);
        if (sizeKb > 8000) { alert(`"${f.name}" is over 8 MB.`); continue; }
        const data_base64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => { const v = r.result; resolve(typeof v === "string" && v.includes(",") ? v.split(",")[1] : v); };
          r.onerror = () => reject(new Error("read failed"));
          r.readAsDataURL(f);
        });
        next.push({ name: f.name, mime: f.type || "application/octet-stream", size_kb: sizeKb, data_base64 });
      }
      setDocuments((prev) => [...prev, ...next].slice(0, 4));
    } catch (err) { alert(err.message); }
    finally { setPickingDoc(false); e.target.value = ""; }
  };

  const removeDocument = (i) => setDocuments((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, household_size: parseInt(form.household_size || "1", 10) || 1, documents };
      if (editId) {
        await api(`/referrals/${editId}`, { method: "PATCH", body: payload });
        alert("Updated! Your changes were saved.");
      } else {
        await api("/referrals", { method: "POST", body: payload });
        alert("Submitted! Your referral was sent to the case team.");
      }
      navigate("/dashboard", { replace: true });
    } catch (e) { alert(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <AppShell>
      <div className="px-5 lg:px-10 py-8 max-w-4xl">
        <div>
          <span className="eyebrow">{editId ? "EDIT REFERRAL" : "SUBMIT A CASE"}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">{editId ? "Edit Referral" : "New Referral"}</h1>
        </div>

        <div className="mt-6 flex items-center">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full grid place-items-center font-extrabold text-xs`} style={{ background: i <= step ? "#D62828" : "#F5F5F5", color: i <= step ? "#fff" : "#5A5A5A" }}>
                  {i < step ? <Check size={14} /> : i + 1}
                </div>
                <span className={`text-[10px] tracking-wider font-bold mt-1`} style={{ color: i === step ? "#D62828" : "#5A5A5A" }}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-0.5 mx-2" style={{ background: i < step ? "#D62828" : "#E8E8E8" }} />}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-8">
          {step === 0 && (
            <div>
              <div className="mb-5">
                <h2 className="font-display text-2xl font-black">Partner Information</h2>
                <p className="text-sm mt-1" style={{ color: "#5A5A5A" }}>Tell us about yourself so we can route compensation.</p>
              </div>
              <Field label="Your Name" value={form.partner_name} onChange={(v) => set("partner_name", v)} testId="partner-name" />
              <Field label="Agency / Uplink (optional)" value={form.partner_agency} onChange={(v) => set("partner_agency", v)} testId="partner-agency" />
              <Field label="Your Phone Number" value={form.partner_phone} onChange={(v) => set("partner_phone", v)} type="tel" testId="partner-phone" />
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="mb-5">
                <h2 className="font-display text-2xl font-black">Client Information</h2>
                <p className="text-sm mt-1" style={{ color: "#5A5A5A" }}>Tell us about the client and their situation.</p>
              </div>
              <Field label="Client Full Name" value={form.client_full_name} onChange={(v) => set("client_full_name", v)} testId="client-name" />
              <Field label="Client Phone" value={form.client_phone} onChange={(v) => set("client_phone", v)} type="tel" testId="client-phone" />
              <Field label="Date of Birth (mm/dd/yyyy)" value={form.date_of_birth} onChange={(v) => set("date_of_birth", v)} testId="client-dob" />
              <ChipGroup label="Gender" options={GENDER_OPTIONS} value={form.gender} onChange={(v) => set("gender", v)} testIdPrefix="gender" />
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Field label="State (e.g. TX)" value={form.resident_state} onChange={(v) => set("resident_state", v.toUpperCase())} maxLength={2} testId="client-state" /></div>
                <div><Field label="Zip" value={form.zip_code} onChange={(v) => set("zip_code", v)} type="number" maxLength={5} testId="client-zip" /></div>
              </div>
              <Field label="Household Size" value={form.household_size} onChange={(v) => set("household_size", v.replace(/[^0-9]/g, ""))} type="number" testId="household-size" />
              <ChipGroup label="Who Needs Coverage?" options={COVERAGE_OPTIONS} value={form.who_needs_coverage} onChange={(v) => set("who_needs_coverage", v)} testIdPrefix="coverage" />
              <ChipGroup label="Estimated Annual Income (ACA)" options={INCOME_OPTIONS} value={form.estimated_annual_income} onChange={(v) => set("estimated_annual_income", v)} testIdPrefix="income" />
              <Field label="Desired Coverage Start (optional)" value={form.coverage_start_date} onChange={(v) => set("coverage_start_date", v)} placeholder="mm/dd/yyyy" testId="coverage-start" />
              <Toggle label="Tobacco user (last 12 months)" value={form.tobacco_user} onChange={(v) => set("tobacco_user", v)} testId="tobacco" />

              <div className="mt-8 mb-5">
                <h2 className="font-display text-2xl font-black">Case Background</h2>
                <p className="text-sm mt-1" style={{ color: "#5A5A5A" }}>Help us find the right pathway for placement.</p>
              </div>
              <Toggle label="Has this client been declined recently?" value={form.declined_recently} onChange={(v) => set("declined_recently", v)} testId="declined" />
              <Field label="Major Health Conditions" value={form.major_health_conditions} onChange={(v) => set("major_health_conditions", v)} multiline placeholder="Diabetes, heart disease, pending surgeries..." testId="conditions" />
              <Field label="Current Prescriptions" value={form.current_prescriptions} onChange={(v) => set("current_prescriptions", v)} multiline placeholder="High-cost or specialty medications..." testId="prescriptions" />
              <Field label="Detailed Case Notes" value={form.detailed_case_notes} onChange={(v) => set("detailed_case_notes", v)} multiline placeholder="Background, declines, prior coverage..." testId="notes" />
              <Field label="Target Monthly Budget" value={form.target_monthly_budget} onChange={(v) => set("target_monthly_budget", v)} placeholder="e.g. $400 - $600/mo" testId="budget" />
              <Field label="Coverage Goals or Networks Needed" value={form.coverage_goals} onChange={(v) => set("coverage_goals", v)} multiline placeholder="Specific doctor, PPO network..." testId="goals" />

              <div className="mt-6 p-4 rounded-2xl border" style={{ background: "#E3F0FB", borderColor: "#2E78C5" }}>
                <h3 className="font-extrabold" style={{ color: "#D62828" }}>Documents (optional)</h3>
                <p className="text-xs mt-1" style={{ color: "#5A5A5A" }}>Attach decline letters, ID photos, or supporting PDFs. Max 4 files, 8 MB each.</p>
                <div className="mt-3 space-y-2">
                  {documents.map((d, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-white rounded-xl border" style={{ borderColor: "#E8E8E8" }}>
                      {d.mime?.startsWith("image/") ? <Image size={16} color="#D62828" /> : <FileText size={16} color="#D62828" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{d.name}</div>
                        <div className="text-[10px]" style={{ color: "#8A8A8A" }}>{d.size_kb < 1024 ? `${d.size_kb} KB` : `${(d.size_kb / 1024).toFixed(1)} MB`}</div>
                      </div>
                      <button type="button" data-testid={`remove-doc-${idx}`} onClick={() => removeDocument(idx)} className="p-1.5"><X size={16} /></button>
                    </div>
                  ))}
                  {documents.length < 4 && (
                    <label data-testid="pick-doc-btn" className="flex items-center justify-center gap-2 p-3 bg-white rounded-xl border-2 border-dashed cursor-pointer font-extrabold text-sm" style={{ borderColor: "#D62828", color: "#D62828" }}>
                      {pickingDoc ? <Loader2 className="animate-spin" size={16} /> : <Paperclip size={16} />}
                      {documents.length === 0 ? "Attach Document" : "Add Another"}
                      <input type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={onPickFiles} />
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="mb-5">
                <h2 className="font-display text-2xl font-black">Review & Submit</h2>
                <p className="text-sm mt-1" style={{ color: "#5A5A5A" }}>Double-check your case before sending.</p>
              </div>
              <ReviewBlock title="Partner" items={[["Name", form.partner_name], ["Agency", form.partner_agency], ["Phone", form.partner_phone]]} />
              <ReviewBlock title="Client" items={[
                ["Name", form.client_full_name], ["Phone", form.client_phone], ["DOB", form.date_of_birth], ["Gender", form.gender],
                ["State / Zip", `${form.resident_state} ${form.zip_code}`], ["Household", form.household_size],
                ["Coverage", form.who_needs_coverage], ["Income", form.estimated_annual_income],
                ["Start Date", form.coverage_start_date], ["Tobacco", form.tobacco_user ? "Yes" : "No"],
              ]} />
              <ReviewBlock title="Case" items={[
                ["Declined Recently", form.declined_recently ? "Yes" : "No"],
                ["Conditions", form.major_health_conditions], ["Prescriptions", form.current_prescriptions],
                ["Notes", form.detailed_case_notes], ["Budget", form.target_monthly_budget], ["Goals", form.coverage_goals],
                ["Documents", documents.length === 0 ? "—" : `${documents.length} attached`],
              ]} />
            </div>
          )}
        </div>

        <div className="sticky bottom-0 -mx-5 lg:-mx-10 px-5 lg:px-10 py-4 mt-8 bg-white/90 backdrop-blur border-t flex gap-3" style={{ borderColor: "#E8E8E8" }}>
          {step > 0 && (
            <button data-testid="step-back-btn" onClick={back} disabled={submitting} className="btn-secondary flex-1 !py-3.5">
              <ArrowLeft size={18} /> Back
            </button>
          )}
          {step < 2 ? (
            <button data-testid="step-next-btn" onClick={next} className={`btn-primary !py-3.5 ${step === 0 ? "flex-1" : "flex-[2]"}`}>
              Next Step <ArrowRight size={18} />
            </button>
          ) : (
            <button data-testid="submit-final-btn" onClick={submit} disabled={submitting} className="btn-primary !py-3.5 flex-[2]">
              {submitting ? <Loader2 className="animate-spin" size={18} /> : (<><CheckCircle2 size={18} /> {editId ? "Save Changes" : "Submit Referral"}</>)}
            </button>
          )}
        </div>
      </div>
    </AppShell>
  );
}
